/**
 * Derivações fiscais no front a partir de fiscal_year_calculations + última
 * fiscal_month_entry com bilan preenchido.
 *
 * O engine F5.4 já calcula FR/BFR/Trésorerie nette/diff. Aqui completamos
 * dois KPIs que ainda não estão no engine: DSO (délai clients) e DPO (délai
 * fournisseurs), via fórmula clássica:
 *
 *   DSO = (creances_clients / ca_ht) × nb_jours_exercice
 *   DPO = (dettes_fournisseurs / achats_consommes) × nb_jours_exercice
 *
 * Retorna `null` quando denominador ≤ 0 ou bilan não preenchido.
 */

import { toNumber } from "./format-fr.js";

type EntryRow = {
  month: number;
  year: number;
  creancesClients: string | null;
  dettesFournisseurs: string | null;
  immobilisationsNettes: string | null;
  autresCreances: string | null;
  tresorerie: string | null;
  capitauxPropres: string | null;
  dettesFinancieres: string | null;
  autresDettes: string | null;
};

/**
 * Retorna a entry mais recente que tem pelo menos UM campo de bilan preenchido.
 * Mesma regra do engine (último entry com balance no FY).
 */
export function findLastBalanceEntry<T extends EntryRow>(entries: ReadonlyArray<T>): T | null {
  const sorted = [...entries].sort((a, b) => b.year - a.year || b.month - a.month);
  for (const e of sorted) {
    if (
      e.immobilisationsNettes !== null ||
      e.creancesClients !== null ||
      e.autresCreances !== null ||
      e.tresorerie !== null ||
      e.capitauxPropres !== null ||
      e.dettesFinancieres !== null ||
      e.dettesFournisseurs !== null ||
      e.autresDettes !== null
    ) {
      return e;
    }
  }
  return null;
}

export type DelaisResult = {
  dso: number | null;
  dpo: number | null;
  bilanMonth: number | null;
  bilanYear: number | null;
};

export function computeDelais(opts: {
  caHt: number | string | null | undefined;
  achatsConsommes: number | string | null | undefined;
  nbJoursExercice: number | string | null | undefined;
  lastBalanceEntry: EntryRow | null;
}): DelaisResult {
  const e = opts.lastBalanceEntry;
  if (!e) {
    return { dso: null, dpo: null, bilanMonth: null, bilanYear: null };
  }

  const caHt = toNumber(opts.caHt) ?? 0;
  const achats = toNumber(opts.achatsConsommes) ?? 0;
  const nbJ = toNumber(opts.nbJoursExercice) ?? 0;
  const creances = toNumber(e.creancesClients);
  const dettes = toNumber(e.dettesFournisseurs);

  const dso = creances !== null && caHt > 0 && nbJ > 0 ? (creances / caHt) * nbJ : null;
  const dpo = dettes !== null && achats > 0 && nbJ > 0 ? (dettes / achats) * nbJ : null;

  return {
    dso,
    dpo,
    bilanMonth: e.month,
    bilanYear: e.year,
  };
}

/**
 * Calcula écart do seuil = ca_ht − seuil_economique.
 * Retorna `null` se seuil é null (margeCv ≤ 0).
 */
export function computeEcartSeuil(opts: {
  caHt: number | string | null | undefined;
  seuilEconomique: number | string | null | undefined;
}): { ecart: number | null; ecartPctOfCa: number | null } {
  const ca = toNumber(opts.caHt);
  const seuil = toNumber(opts.seuilEconomique);
  if (ca === null || seuil === null) {
    return { ecart: null, ecartPctOfCa: null };
  }
  const ecart = ca - seuil;
  const ecartPctOfCa = ca > 0 ? (ecart / ca) * 100 : null;
  return { ecart, ecartPctOfCa };
}

/**
 * Alertas visuais do PRD §10. Cada chave indica se o alerta dispara.
 * Retorna `null` para o alerta quando não há dado suficiente para avaliá-lo.
 */
export type FiscalAlerts = {
  margeBasse: { triggered: boolean; tauxMarge: number | null } | null;
  delaiClientsAlto: { triggered: boolean; dso: number | null } | null;
  tresorerieNegativa: { triggered: boolean; tresorerie: number | null } | null;
  ecartSeuilEstreito: {
    triggered: boolean;
    ecartPctOfCa: number | null;
  } | null;
  chargesPersonnelAltas: {
    triggered: boolean;
    ratio: number | null;
  } | null;
};

export function computeFiscalAlerts(opts: {
  caHt: number | string | null | undefined;
  margeGlobaleCumulative: number | string | null | undefined;
  chargesPersonnel: number | string | null | undefined;
  vaCumulative: number | string | null | undefined;
  tresorerieEntry: number | string | null | undefined;
  ecartPctOfCa: number | null;
  dso: number | null;
}): FiscalAlerts {
  const ca = toNumber(opts.caHt) ?? 0;
  const marge = toNumber(opts.margeGlobaleCumulative);
  const personnel = toNumber(opts.chargesPersonnel);
  const va = toNumber(opts.vaCumulative);
  const tresor = toNumber(opts.tresorerieEntry);

  const tauxMarge = marge !== null && ca > 0 ? (marge / ca) * 100 : null;
  const ratioPersonnel =
    personnel !== null && va !== null && va > 0 ? (personnel / va) * 100 : null;

  return {
    margeBasse: tauxMarge === null ? null : { triggered: tauxMarge < 15, tauxMarge },
    delaiClientsAlto: opts.dso === null ? null : { triggered: opts.dso > 60, dso: opts.dso },
    tresorerieNegativa: tresor === null ? null : { triggered: tresor < 0, tresorerie: tresor },
    ecartSeuilEstreito:
      opts.ecartPctOfCa === null
        ? null
        : {
            triggered: opts.ecartPctOfCa < 10,
            ecartPctOfCa: opts.ecartPctOfCa,
          },
    chargesPersonnelAltas:
      ratioPersonnel === null ? null : { triggered: ratioPersonnel > 35, ratio: ratioPersonnel },
  };
}
